<script setup>
import { computed } from "vue";

const props = defineProps({
  displayed: {
    type: Boolean,
    default: false,
    required: true
  },
  size: {
    type: Number,
    default: 4,
    required: false
  },
  formtype: {
    type: String,
    default: null
  },
  // stretch to the full width of the parent (card-style loaders)
  full: {
    type: Boolean,
    default: false
  },
  // "blue" is the historic name for the brand-coloured variant; "white" is for
  // solid coloured backgrounds (inside filled buttons).
  color: {
    type: String,
    default: "blue"
  }
});

/**
 * A climbing bar chart rather than a generic spinner: the product exists to move
 * a server up a toplist ranking, so the loader shows bars stepping upward. Bars
 * also degrade well — at 16px in a button they still read as "working", where a
 * detailed glyph would turn to mush.
 */
const BAR_COUNT = 5;

// legacy "bars" loaders were block-level table/page loaders that ignored size,
// so render those at a fixed comfortable height
const isLegacyBars = computed(() => props.formtype === "bars");

const height = computed(() => (isLegacyBars.value ? 8 : props.size) * 4);

// Derive bar geometry from the height so the loader keeps its proportions from
// 16px (inside a button) up to 40px (card loader).
const barWidth = computed(() => Math.max(2, Math.round(height.value / 7)));
const gap = computed(() => Math.max(1, Math.round(height.value / 11)));

const bars = computed(() =>
  Array.from({ length: BAR_COUNT }, (_, i) => ({
    // Resting heights ascend left to right so the silhouette reads as a rising
    // chart even in the instant before the animation starts.
    peak: 40 + (i * 60) / (BAR_COUNT - 1),
    delay: i * 110
  }))
);

const isWhite = computed(() => props.color === "white");

const rootStyle = computed(() => ({
  height: `${height.value}px`,
  gap: `${gap.value}px`,
  ...(props.full || isLegacyBars.value ? { width: "100%" } : {})
}));

const barStyle = (bar) => ({
  width: `${barWidth.value}px`,
  borderRadius: `${Math.max(1, Math.round(barWidth.value / 2))}px`,
  "--peak": `${bar.peak}%`,
  animationDelay: `${bar.delay}ms`
});
</script>

<template>
  <div
    v-show="props.displayed"
    class="rank-loader"
    :class="[
      isLegacyBars ? 'rank-loader--spaced' : '',
      isWhite ? 'rank-loader--white' : 'rank-loader--brand'
    ]"
    :style="rootStyle"
    role="status"
    aria-label="Loading"
  >
    <span
      v-for="(bar, index) in bars"
      :key="index"
      class="rank-loader-bar"
      :style="barStyle(bar)"
    />
  </div>
</template>

<style scoped>
.rank-loader {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin: 0 auto;
}

.rank-loader--spaced {
  margin: 20px auto;
}

.rank-loader-bar {
  display: block;
  height: var(--peak);
  transform-origin: bottom center;
  animation: rank-climb 1.15s ease-in-out infinite;
}

/* Brand-600 at the foot to brand-400 at the top: top-lit, so the bars read as
   solid objects rather than flat rectangles. Read from the --brand-*
   variables, so the loader follows whichever brand colour is chosen. */
.rank-loader--brand .rank-loader-bar {
  background: linear-gradient(to top, rgb(var(--brand-600)), rgb(var(--brand-400)));
}

.rank-loader--white .rank-loader-bar {
  background: linear-gradient(to top, rgba(255, 255, 255, 0.75), #ffffff);
}

@keyframes rank-climb {
  0%,
  100% {
    transform: scaleY(0.35);
    opacity: 0.55;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}

/* The loader still has to communicate "working", so it fades in place instead
   of losing its animation entirely. */
@media (prefers-reduced-motion: reduce) {
  .rank-loader-bar {
    animation: rank-pulse 1.6s ease-in-out infinite;
    transform: none;
  }

  @keyframes rank-pulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
  }
}
</style>
